import { useRef, useEffect, useCallback, useState } from 'react';
import { createFocusTrap, createFocusRestorer } from '../utils/accessibility';

/**
 * Custom hook for comprehensive focus management
 * 
 * Provides focus management utilities for:
 * - Modal/dialog focus trapping
 * - Focus restoration after modal close
 * - Auto-focus on component mount
 * - Focus visible state management
 * - Programmatic focus control
 * - WCAG 2.1 AA compliant focus handling
 * 
 * @param {Object} options - Focus management configuration
 * @param {boolean} [options.trapFocus=false] - Enable focus trapping
 * @param {boolean} [options.restoreFocus=true] - Restore focus on unmount/deactivate
 * @param {boolean} [options.autoFocus=false] - Auto-focus first element on mount
 * @param {string} [options.initialFocusSelector] - Selector for initial focus element
 * @param {Function} [options.onFocusEnter] - Callback when focus enters container
 * @param {Function} [options.onFocusLeave] - Callback when focus leaves container
 * @param {boolean} [options.preventScroll=false] - Prevent scrolling when focusing
 * @param {Array} [options.excludeSelectors] - Selectors to exclude from focus trap
 * @returns {Object} Focus management state and utilities
 */
const useFocusManagement = ({
  trapFocus = false,
  restoreFocus = true,
  autoFocus = false,
  initialFocusSelector,
  onFocusEnter,
  onFocusLeave,
  preventScroll = false,
  excludeSelectors = []
} = {}) => {
  
  const containerRef = useRef(null);
  const focusTrapRef = useRef(null);
  const previousActiveElementRef = useRef(null);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const [focusedElement, setFocusedElement] = useState(null);
  const [isActive, setIsActive] = useState(false);

  // Track focus-visible state (keyboard vs mouse focus)
  useEffect(() => {
    let isKeyboardUser = false;

    const handleKeyDown = (event) => {
      if (event.key === 'Tab' || event.key === 'Enter' || event.key === ' ') {
        isKeyboardUser = true;
      }
    };

    const handleMouseDown = () => {
      isKeyboardUser = false;
    };

    const handleFocus = (event) => {
      if (containerRef.current?.contains(event.target)) {
        setIsFocusVisible(isKeyboardUser);
        setFocusedElement(event.target);
      }
    };

    const handleBlur = (event) => {
      if (containerRef.current?.contains(event.target)) {
        setIsFocusVisible(false);
        setFocusedElement(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleBlur, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleBlur, true);
    };
  }, []);

  // Get focusable elements within container
  const getFocusableElements = useCallback(() => {
    if (!containerRef.current) return [];

    const focusableSelector = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]'
    ].join(', ');

    const elements = Array.from(containerRef.current.querySelectorAll(focusableSelector));
    
    // Filter out excluded elements
    return elements.filter(element => {
      return !excludeSelectors.some(selector => element.matches(selector));
    });
  }, [excludeSelectors]);

  // Get first focusable element
  const getFirstFocusableElement = useCallback(() => {
    if (initialFocusSelector && containerRef.current) {
      const specificElement = containerRef.current.querySelector(initialFocusSelector);
      if (specificElement) return specificElement;
    }
    
    const focusableElements = getFocusableElements();
    return focusableElements[0] || null;
  }, [initialFocusSelector, getFocusableElements]);

  // Get last focusable element
  const getLastFocusableElement = useCallback(() => {
    const focusableElements = getFocusableElements();
    return focusableElements[focusableElements.length - 1] || null;
  }, [getFocusableElements]);

  // Focus an element with options
  const focusElement = useCallback((element, options = {}) => {
    if (!element || typeof element.focus !== 'function') return false;

    try {
      element.focus({
        preventScroll: options.preventScroll ?? preventScroll,
        ...options
      });
      return true;
    } catch (error) {
      console.warn('Focus management: Failed to focus element', error);
      return false;
    }
  }, [preventScroll]);

  // Focus first element
  const focusFirst = useCallback((options = {}) => {
    const firstElement = getFirstFocusableElement();
    return focusElement(firstElement, options);
  }, [getFirstFocusableElement, focusElement]);

  // Focus last element
  const focusLast = useCallback((options = {}) => {
    const lastElement = getLastFocusableElement();
    return focusElement(lastElement, options);
  }, [getLastFocusableElement, focusElement]);

  // Focus next element in tab order
  const focusNext = useCallback((options = {}) => {
    const focusableElements = getFocusableElements();
    const currentIndex = focusableElements.indexOf(document.activeElement);
    
    if (currentIndex === -1) {
      return focusFirst(options);
    }
    
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    return focusElement(focusableElements[nextIndex], options);
  }, [getFocusableElements, focusFirst, focusElement]);

  // Focus previous element in tab order
  const focusPrevious = useCallback((options = {}) => {
    const focusableElements = getFocusableElements();
    const currentIndex = focusableElements.indexOf(document.activeElement);
    
    if (currentIndex === -1) {
      return focusLast(options);
    }
    
    const previousIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
    return focusElement(focusableElements[previousIndex], options);
  }, [getFocusableElements, focusLast, focusElement]);

  // Activate focus management
  const activate = useCallback(() => {
    if (isActive) return;

    // Store currently focused element for restoration
    previousActiveElementRef.current = document.activeElement;
    
    // Set up focus trap if enabled
    if (trapFocus && containerRef.current) {
      focusTrapRef.current = createFocusTrap(containerRef.current);
      focusTrapRef.current?.activate();
    } else if (autoFocus) {
      // Just auto-focus without trapping
      setTimeout(() => focusFirst(), 0);
    }

    setIsActive(true);
    onFocusEnter?.();
  }, [isActive, trapFocus, autoFocus, focusFirst, onFocusEnter]);

  // Deactivate focus management
  const deactivate = useCallback(() => {
    if (!isActive) return;

    // Clean up focus trap
    if (focusTrapRef.current) {
      focusTrapRef.current.deactivate();
      focusTrapRef.current = null;
    }

    // Restore focus if enabled
    if (restoreFocus && previousActiveElementRef.current) {
      const restoreFocusTo = previousActiveElementRef.current;
      setTimeout(() => {
        if (restoreFocusTo && typeof restoreFocusTo.focus === 'function') {
          try {
            restoreFocusTo.focus({ preventScroll });
          } catch (error) {
            console.warn('Focus management: Failed to restore focus', error);
          }
        }
      }, 0);
    }

    setIsActive(false);
    onFocusLeave?.();
  }, [isActive, restoreFocus, preventScroll, onFocusLeave]);

  // Handle container focus events
  const handleContainerFocusIn = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      if (!isActive) {
        activate();
      }
    }
  }, [isActive, activate]);

  const handleContainerFocusOut = useCallback((event) => {
    if (containerRef.current && !containerRef.current.contains(event.relatedTarget)) {
      if (isActive && !trapFocus) {
        deactivate();
      }
    }
  }, [isActive, trapFocus, deactivate]);

  // Keyboard event handler for focus management
  const handleKeyDown = useCallback((event) => {
    if (!isActive) return;

    const { key, shiftKey, ctrlKey, metaKey } = event;

    switch (key) {
      case 'Tab':
        if (trapFocus) {
          // Let the focus trap handle Tab navigation
          return;
        }
        break;

      case 'Escape':
        if (trapFocus) {
          event.preventDefault();
          deactivate();
        }
        break;

      case 'Home':
        if (ctrlKey || metaKey) {
          event.preventDefault();
          focusFirst();
        }
        break;

      case 'End':
        if (ctrlKey || metaKey) {
          event.preventDefault();
          focusLast();
        }
        break;

      default:
        break;
    }
  }, [isActive, trapFocus, deactivate, focusFirst, focusLast]);

  // Auto-activate on mount if specified
  useEffect(() => {
    if (autoFocus || trapFocus) {
      activate();
    }

    return () => {
      if (isActive) {
        deactivate();
      }
    };
  }, []); // Only run on mount/unmount

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (focusTrapRef.current) {
        focusTrapRef.current.deactivate();
      }
    };
  }, []);

  // Check if element is focusable
  const isFocusable = useCallback((element) => {
    if (!element) return false;
    
    const focusableElements = getFocusableElements();
    return focusableElements.includes(element);
  }, [getFocusableElements]);

  // Get focus state information
  const getFocusState = useCallback(() => {
    const focusableElements = getFocusableElements();
    const activeElement = document.activeElement;
    const isWithinContainer = containerRef.current?.contains(activeElement);
    const currentIndex = isWithinContainer ? focusableElements.indexOf(activeElement) : -1;

    return {
      isActive,
      isFocusVisible,
      focusedElement,
      isWithinContainer,
      currentIndex,
      totalFocusable: focusableElements.length,
      canFocusNext: currentIndex < focusableElements.length - 1,
      canFocusPrevious: currentIndex > 0
    };
  }, [isActive, isFocusVisible, focusedElement, getFocusableElements]);

  // Create focus restoration function
  const createRestorer = useCallback((element = document.activeElement) => {
    return createFocusRestorer(element);
  }, []);

  return {
    // Refs
    containerRef,
    
    // State
    isActive,
    isFocusVisible,
    focusedElement,
    
    // Focus control methods
    activate,
    deactivate,
    focusFirst,
    focusLast,
    focusNext,
    focusPrevious,
    focusElement,
    
    // Event handlers
    handleContainerFocusIn,
    handleContainerFocusOut,
    handleKeyDown,
    
    // Utilities
    getFocusableElements,
    getFirstFocusableElement,
    getLastFocusableElement,
    isFocusable,
    getFocusState,
    createRestorer,
    
    // Configuration
    trapFocus,
    restoreFocus,
    autoFocus
  };
};

/**
 * Simplified hook for modal/dialog focus management
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {Object} options - Focus options
 * @returns {Object} Modal focus management
 */
export const useModalFocus = (isOpen, options = {}) => {
  const focusManagement = useFocusManagement({
    trapFocus: true,
    restoreFocus: true,
    autoFocus: true,
    ...options
  });

  useEffect(() => {
    if (isOpen) {
      focusManagement.activate();
    } else {
      focusManagement.deactivate();
    }
  }, [isOpen, focusManagement.activate, focusManagement.deactivate]);

  return focusManagement;
};

/**
 * Hook for managing focus in tab panels
 * 
 * @param {number} activeTab - Currently active tab index
 * @param {Object} options - Focus options
 * @returns {Object} Tab focus management
 */
export const useTabFocus = (activeTab, options = {}) => {
  const focusManagement = useFocusManagement({
    autoFocus: false,
    trapFocus: false,
    restoreFocus: false,
    ...options
  });

  const focusTab = useCallback((tabIndex) => {
    if (focusManagement.containerRef.current) {
      const tabButton = focusManagement.containerRef.current.querySelector(
        `[role="tab"][data-index="${tabIndex}"]`
      );
      if (tabButton) {
        focusManagement.focusElement(tabButton);
      }
    }
  }, [focusManagement]);

  const focusPanel = useCallback((panelIndex = activeTab) => {
    if (focusManagement.containerRef.current) {
      const tabPanel = focusManagement.containerRef.current.querySelector(
        `[role="tabpanel"][data-index="${panelIndex}"]`
      );
      if (tabPanel) {
        // Focus first focusable element in panel
        const firstFocusable = tabPanel.querySelector(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (firstFocusable) {
          focusManagement.focusElement(firstFocusable);
        } else {
          focusManagement.focusElement(tabPanel);
        }
      }
    }
  }, [activeTab, focusManagement]);

  return {
    ...focusManagement,
    focusTab,
    focusPanel
  };
};

export default useFocusManagement;